import React from 'react';
import { Map, Source, Layer, Marker, Popup, NavigationControl } from 'react-map-gl/mapbox';
import mapboxgl from 'mapbox-gl';
import buffer from '@turf/buffer';
import useSupercluster from 'use-supercluster';
import { useQueryClient } from '@tanstack/react-query';
import { Card, Button, Form, Spinner, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { MapPin, Users, Radio, AlertTriangle, X, ChevronLeft } from 'lucide-react';

import PrivateLayout from '../layouts/PrivateLayout';
import {
  fetchClientDetail,
  useCoverageMapPoints,
  useCoverageMapUncovered,
  useUpdateProviderRadius,
} from '../../services/coverageMap';
import { useUser } from '../../contexts/UserContext';

import 'mapbox-gl/dist/mapbox-gl.css';

// Mapbox renders the base tiles via mapbox-gl. Style URL uses Mapbox's
// canonical `mapbox://` form; swap to a custom Mapbox Studio style later if
// we ever want a branded look. The token is a public `pk.` Mapbox token;
// client-side exposure is expected and safe (URL-restrict it in Mapbox if
// abuse appears).
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
// Two styles available via the panel's "MAP" toggle. Both extend Mapbox
// Standard so the time-of-day lightPreset config keeps working on either.
const MAP_STYLES = {
  faded: 'mapbox://styles/mapbox/standard',
  mono: 'mapbox://styles/bakesmash/cmqj07t9h004801s6gcnb6xxf',
};
const DEFAULT_MAP_STYLE_KEY = 'faded';

const INITIAL_VIEW = {
  longitude: -97.5,
  latitude: 38.5,
  zoom: 3.5,
};

// Violet reads cleanly against both light and dark Mapbox basemaps and gives
// Curated overlay palette. Violet is the default — reads cleanly against
// both light and dark Mapbox basemaps. User picks from these via the color
// dot in the panel header.
const OVERLAY_COLORS = [
  { value: '#8b5cf6', label: 'Violet' },
  { value: '#10b981', label: 'Emerald' },
  { value: '#06b6d4', label: 'Cyan' },
  { value: '#ef4444', label: 'Red' },
];
const DEFAULT_OVERLAY_COLOR = OVERLAY_COLORS[0].value;

const CLIENT_COLOR = '#3b82f6'; // blue
const UNCOVERED_COLOR = '#dc2626'; // red

// Uncovered set is computed server-side now (Haversine in PostgreSQL) — see
// GET /api/coverage-map/uncovered. The browser receives a list of userIds.

// Convert a provider center + radius (miles) into a circle polygon GeoJSON
// feature using turf/buffer. 64 steps is plenty smooth at any zoom.
const providerCirclesFeatureCollection = (providers) => ({
  type: 'FeatureCollection',
  features: providers.map((p) => {
    const point = {
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [p.longitude, p.latitude] },
      properties: { userId: p.userId },
    };
    const circle = buffer(point, p.radiusMiles || 50, {
      units: 'miles',
      steps: 64,
    });
    circle.properties = { userId: p.userId, radiusMiles: p.radiusMiles };
    return circle;
  }),
});

// ---------- Premium-panel building blocks ----------------------------------

const SectionLabel = ({ children }) => (
  <div
    style={{
      fontSize: 10.5,
      fontWeight: 600,
      color: '#94a3b8',
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      marginBottom: 8,
    }}
  >
    {children}
  </div>
);

const Chip = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      border: '1px solid',
      borderColor: active ? 'transparent' : 'rgba(15, 23, 42, 0.1)',
      background: active ? '#0f172a' : '#fff',
      color: active ? '#fff' : '#475569',
      padding: '3px 9px',
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 120ms ease',
      lineHeight: 1.4,
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </button>
);

const Segmented = ({ value, onChange, options }) => (
  <div
    style={{
      display: 'flex',
      background: 'rgba(15, 23, 42, 0.04)',
      borderRadius: 8,
      padding: 3,
      gap: 2,
    }}
  >
    {options.map((opt) => {
      const isActive = value === opt.value;
      return (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          style={{
            flex: 1,
            border: 'none',
            background: isActive ? '#0f172a' : 'transparent',
            color: isActive ? '#fff' : '#64748b',
            padding: '5px 8px',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: isActive ? 600 : 500,
            cursor: 'pointer',
            transition: 'all 120ms ease',
            boxShadow: isActive
              ? '0 1px 2px rgba(15, 23, 42, 0.15)'
              : 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            whiteSpace: 'nowrap',
          }}
        >
          {opt.label}
          {opt.busy && (
            <Spinner
              animation="border"
              size="sm"
              style={{ width: 10, height: 10, borderWidth: 1.5 }}
            />
          )}
        </button>
      );
    })}
  </div>
);

// Click the displayed "N mi" to edit it inline. Enter or blur commits.
// Escape reverts. Tabular numerals so the number doesn't jitter while typing.
const InlineRadius = ({ value, baseValue, disabled, onCommit }) => {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(String(value));
  const inputRef = React.useRef(null);
  React.useEffect(() => {
    if (!editing) setDraft(String(value));
  }, [value, editing]);
  React.useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);
  if (editing) {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'baseline',
          gap: 2,
          fontWeight: 600,
          color: '#0f172a',
          fontSize: 13,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={3}
          value={draft}
          onChange={(e) =>
            setDraft(e.target.value.replace(/[^0-9]/g, '').slice(0, 3))
          }
          onBlur={() => {
            const n = Number(draft);
            if (Number.isFinite(n) && n > 0) onCommit(n);
            setEditing(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              const n = Number(draft);
              if (Number.isFinite(n) && n > 0) onCommit(n);
              setEditing(false);
            } else if (e.key === 'Escape') {
              setDraft(String(baseValue ?? value));
              setEditing(false);
            }
          }}
          style={{
            width: 40,
            textAlign: 'right',
            border: 'none',
            background: 'rgba(15, 23, 42, 0.06)',
            borderRadius: 4,
            fontWeight: 600,
            color: '#0f172a',
            fontSize: 13,
            fontVariantNumeric: 'tabular-nums',
            padding: '1px 4px',
            outline: 'none',
          }}
        />
        <span>mi</span>
      </span>
    );
  }
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => !disabled && setEditing(true)}
      style={{
        border: 'none',
        background: 'transparent',
        padding: '1px 4px',
        margin: '-1px -4px',
        borderRadius: 4,
        fontWeight: 600,
        color: '#0f172a',
        fontSize: 13,
        fontVariantNumeric: 'tabular-nums',
        cursor: disabled ? 'default' : 'pointer',
        transition: 'background 120ms ease',
      }}
      onMouseEnter={(e) => {
        if (!disabled) e.currentTarget.style.background = 'rgba(15, 23, 42, 0.06)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
      title={disabled ? '' : 'Click to edit'}
    >
      {value} mi
    </button>
  );
};

const StatTile = ({ label, value, color, emphasize = false }) => (
  <div
    style={{
      padding: '10px 8px',
      background: emphasize ? `${color}0d` : 'transparent',
      borderRadius: 10,
      border: emphasize ? `1px solid ${color}33` : '1px solid transparent',
      minWidth: 0,
      overflow: 'hidden',
    }}
  >
    <div
      style={{
        fontSize: 9.5,
        fontWeight: 600,
        color: '#94a3b8',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        marginBottom: 4,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontSize: 20,
        fontWeight: 600,
        color: emphasize ? color : '#0f172a',
        letterSpacing: '-0.02em',
        fontVariantNumeric: 'tabular-nums',
        lineHeight: 1,
      }}
    >
      {value}
    </div>
  </div>
);

const CoverageMap = () => {
  const { hasPermission } = useUser();
  const canEditRadius = hasPermission('coverage_map:edit_radius');

  // Two independent layer toggles. Default: both on.
  const [showProviders, setShowProviders] = React.useState(true);
  const [showClients, setShowClients] = React.useState(true);
  const [selectedProviderId, setSelectedProviderId] = React.useState(null);
  const [selectedClientId, setSelectedClientId] = React.useState(null);
  // Cache fetched client details so re-selecting one is instant.
  const [clientDetails, setClientDetails] = React.useState({});
  const selectedClient = clientDetails[selectedClientId] || null;
  const [viewState, setViewState] = React.useState(INITIAL_VIEW);
  const [bounds, setBounds] = React.useState(null);
  const mapRef = React.useRef(null);
  // Mapbox Standard style time-of-day preset. Applied via setConfigProperty
  // whenever it changes (and once on map load via onStyleData).
  const [lightPreset, setLightPreset] = React.useState('day');
  const [providerColor, setProviderColor] = React.useState(DEFAULT_OVERLAY_COLOR);
  const [panelOpen, setPanelOpen] = React.useState(true);
  const [mapStyleKey, setMapStyleKey] = React.useState(DEFAULT_MAP_STYLE_KEY);

  // Re-apply lightPreset both on initial style load AND after a style swap
  // (Mapbox resets config state when the style changes). isStyleLoaded() is
  // the safe gate; otherwise wait for the next style.load event.
  React.useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    const apply = () => {
      try {
        map.setConfigProperty('basemap', 'lightPreset', lightPreset);
      } catch (_) {
        // Custom styles may not expose the `basemap` config source — fine.
      }
    };
    if (map.isStyleLoaded()) apply();
    else map.once('style.load', apply);
  }, [lightPreset, mapStyleKey]);

  // Slim payload: client rows ship only userId + lat/lng. Names + city
  // hydrate per-click via fetchClientDetail. Providers stay rich (small set,
  // panel always needs names).
  const { data, isLoading, isError } = useCoverageMapPoints({ slim: 'true' });
  const providers = React.useMemo(() => data?.providers || [], [data]);
  const clients = React.useMemo(() => data?.clients || [], [data]);

  // Live draft for the selected provider only. The bulk `effectiveProviders`
  // is the SERVER data (mutation-invalidated, refreshed on release) — so the
  // big O(providers × clients) memos below do NOT re-fire on every slider
  // tick. The selected provider's live ring is drawn from `draftRadius`
  // separately and only re-buffers ONE circle per tick.
  const [draftRadius, setDraftRadius] = React.useState(null); // number | null
  const queryClient = useQueryClient();

  const updateRadius = useUpdateProviderRadius(
    () => {
      // Refresh server data so the bulk memos pick up the new value, then
      // clear the draft so the selected ring reads from server again.
      queryClient.invalidateQueries(['coverage-map-points']);
      queryClient.invalidateQueries(['coverage-map-uncovered']);
      setDraftRadius(null);
    },
    {
      onError: (err) => {
        // Keep the draft in place so the slider holds the user's attempted
        // value (they can retry). Resetting it would snap the slider back to
        // the server value mid-interaction, which is a jarring UX jolt.
        toast.error(err?.message || 'Failed to save coverage radius.');
      },
    },
  );
  const persistRadius = React.useCallback(
    (userId, radiusMiles) => {
      updateRadius.mutate({ userId, radiusMiles });
    },
    [updateRadius],
  );

  const effectiveProviders = providers;

  // Server-side Haversine (used in circle mode) — scales to 10k clients × 500
  // providers without freezing the browser. Returns Set<userId> of uncovered
  // clients per the straight-line circle.
  const { data: uncoveredData } = useCoverageMapUncovered();
  const circleUncoveredSet = React.useMemo(
    () => new Set(uncoveredData?.uncovered || []),
    [uncoveredData],
  );

  const uncoveredSet = circleUncoveredSet;

  // Filter the visible sets up-front so the cluster source matches what the
  // panel summary shows. Each chip toggles independently.
  const visibleProviders = showProviders ? effectiveProviders : [];
  const visibleClients = showClients ? clients : [];

  const clientPoints = React.useMemo(
    () =>
      visibleClients.map((c) => ({
        type: 'Feature',
        properties: {
          cluster: false,
          userId: c.userId,
          uncovered: uncoveredSet.has(c.userId),
        },
        geometry: {
          type: 'Point',
          coordinates: [c.longitude, c.latitude],
        },
      })),
    [visibleClients, uncoveredSet],
  );

  const { clusters, supercluster } = useSupercluster({
    points: clientPoints,
    bounds,
    zoom: viewState.zoom,
    options: { radius: 60, maxZoom: 14 },
  });

  // Bulk circles: every visible provider EXCEPT the selected one (whose ring
  // is drawn from `draftRadius` in a separate Source so it can redraw live
  // during slider drag without invalidating the bulk geometry).
  const bulkVisibleProviders = React.useMemo(
    () => visibleProviders.filter((p) => p.userId !== selectedProviderId),
    [visibleProviders, selectedProviderId],
  );
  const bulkCirclesGeo = React.useMemo(
    () => providerCirclesFeatureCollection(bulkVisibleProviders),
    [bulkVisibleProviders],
  );

  const selectedBaseProvider = React.useMemo(
    () => effectiveProviders.find((p) => p.userId === selectedProviderId) || null,
    [effectiveProviders, selectedProviderId],
  );
  // `selectedProvider` overlays the live draft radius on top of the server
  // radius so the panel readout and live ring update together.
  const selectedProvider = React.useMemo(() => {
    if (!selectedBaseProvider) return null;
    if (draftRadius != null) {
      return { ...selectedBaseProvider, radiusMiles: draftRadius };
    }
    return selectedBaseProvider;
  }, [selectedBaseProvider, draftRadius]);

  // Live ring for selected provider only — one buffer call per tick.
  const liveSelectedCircleGeo = React.useMemo(() => {
    if (!selectedProvider) return { type: 'FeatureCollection', features: [] };
    if (!showProviders) {
      return { type: 'FeatureCollection', features: [] };
    }
    return providerCirclesFeatureCollection([selectedProvider]);
  }, [selectedProvider, showProviders]);

  const onMapMove = React.useCallback((evt) => {
    setViewState(evt.viewState);
    const map = mapRef.current?.getMap();
    if (map) {
      const b = map.getBounds();
      setBounds([b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]);
    }
  }, []);

  const flyTo = (lng, lat, zoom = 11) => {
    mapRef.current?.getMap().flyTo({ center: [lng, lat], zoom, duration: 600 });
  };

  // Key gate: no Mapbox token, no map. Tell the dev what to do; show a
  // generic message in prod so we never leak dev jargon to end users.
  if (!MAPBOX_TOKEN) {
    const isDev = process.env.NODE_ENV !== 'production';
    return (
      <PrivateLayout>
        <div className="container my-5">
          <Card className="shadow-sm">
            <Card.Body>
              <h4>Coverage Map</h4>
              {isDev ? (
                <p className="text-muted">
                  Set <code>REACT_APP_MAPBOX_ACCESS_TOKEN</code> in your{' '}
                  <code>.env</code> and restart <code>npm run start:ui</code>{' '}
                  to load the map.
                </p>
              ) : (
                <p className="text-muted">
                  The map service is temporarily unavailable. Please try again
                  in a few minutes or contact support if this persists.
                </p>
              )}
            </Card.Body>
          </Card>
        </div>
      </PrivateLayout>
    );
  }

  return (
    <PrivateLayout>
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: 'calc(100vh - 130px)',
          minHeight: 500,
          overflow: 'hidden',
          borderRadius: 12,
          boxShadow: '0 6px 24px rgba(0,0,0,0.08)',
        }}
      >
        <Map
          ref={mapRef}
          mapLib={mapboxgl}
          mapboxAccessToken={MAPBOX_TOKEN}
          mapStyle={MAP_STYLES[mapStyleKey]}
          initialViewState={INITIAL_VIEW}
          onMove={onMapMove}
          // Background click → deselect everything. Marker onClicks call
          // stopPropagation, so this only fires when the user clicks empty map.
          onClick={() => {
            setSelectedProviderId(null);
            setSelectedClientId(null);
            setDraftRadius(null);
          }}
          onLoad={(e) => {
            const b = e.target.getBounds();
            setBounds([b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]);
            // Apply the active lightPreset once the Mapbox Standard basemap
            // finishes loading (this is the first moment setConfigProperty
            // accepts the call).
            try {
              e.target.setConfigProperty('basemap', 'lightPreset', lightPreset);
            } catch (_) {}
          }}
          style={{ width: '100%', height: '100%' }}
        >
          <NavigationControl position="top-right" />

          {/* Bulk provider coverage circles (excludes selected; stable across drag) */}
          <Source id="provider-circles" type="geojson" data={bulkCirclesGeo}>
            {/* Bulk providers: ultra-faint fill so overlapping circles
                don't compound into an unreadable blob. Outline carries the
                visual weight — contour-map style. */}
            <Layer
              id="provider-circles-fill"
              type="fill"
              paint={{
                'fill-color': providerColor,
                'fill-opacity': 0.04,
                // Self-emissive — ignore Mapbox Standard's 3D ambient lighting
                // so the color stays uniform across day/dusk/night presets.
                'fill-emissive-strength': 1,
              }}
            />
            <Layer
              id="provider-circles-outline"
              type="line"
              paint={{
                'line-color': providerColor,
                'line-width': 1.5,
                'line-opacity': 0.65,
                'line-emissive-strength': 1,
              }}
            />
          </Source>

          {/* Selected provider live ring — one circle, redraws on every drag tick */}
          <Source
            id="provider-circle-selected"
            type="geojson"
            data={liveSelectedCircleGeo}
          >
            <Layer
              id="provider-circle-selected-fill"
              type="fill"
              paint={{
                'fill-color': providerColor,
                'fill-opacity': 0.28,
                'fill-emissive-strength': 1,
              }}
            />
            <Layer
              id="provider-circle-selected-outline"
              type="line"
              paint={{
                'line-color': providerColor,
                'line-width': 3,
                'line-opacity': 1,
                'line-emissive-strength': 1,
              }}
            />
          </Source>

          {/* Provider dots */}
          {visibleProviders.map((p) => (
            <Marker
              key={`prov-${p.userId}`}
              longitude={p.longitude}
              latitude={p.latitude}
              anchor="center"
              onClick={(e) => {
                e.originalEvent.stopPropagation();
                setSelectedProviderId(p.userId);
                setSelectedClientId(null);
                // Clear any stale draft from a previous provider's failed
                // save — otherwise the new provider's panel + live ring
                // briefly show the prior radius until the user interacts.
                setDraftRadius(null);
              }}
            >
              <div
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  background: providerColor,
                  border: '3px solid #fff',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.35)',
                  cursor: 'pointer',
                }}
                title={`${p.firstName} ${p.lastName}`}
              />
            </Marker>
          ))}

          {/* Client clusters + individual client dots */}
          {clusters.map((cluster) => {
            const [longitude, latitude] = cluster.geometry.coordinates;
            const { cluster: isCluster, point_count: pointCount } =
              cluster.properties;

            if (isCluster) {
              const size = 26 + Math.min(pointCount, 200) / 8;
              return (
                <Marker
                  key={`cluster-${cluster.id}`}
                  longitude={longitude}
                  latitude={latitude}
                  anchor="center"
                  onClick={(e) => {
                    e.originalEvent.stopPropagation();
                    const targetZoom = Math.min(
                      supercluster.getClusterExpansionZoom(cluster.id),
                      18,
                    );
                    flyTo(longitude, latitude, targetZoom);
                  }}
                >
                  <div
                    style={{
                      width: size,
                      height: size,
                      borderRadius: '50%',
                      background: CLIENT_COLOR,
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 600,
                      fontSize: 12,
                      border: '2px solid #fff',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                      cursor: 'pointer',
                    }}
                  >
                    {pointCount}
                  </div>
                </Marker>
              );
            }

            const dotColor = cluster.properties.uncovered
              ? UNCOVERED_COLOR
              : CLIENT_COLOR;

            return (
              <Marker
                key={`client-${cluster.properties.userId}`}
                longitude={longitude}
                latitude={latitude}
                anchor="center"
                onClick={(e) => {
                  e.originalEvent.stopPropagation();
                  const id = cluster.properties.userId;
                  const uncovered = cluster.properties.uncovered;
                  setSelectedClientId(id);
                  setSelectedProviderId(null);
                  setDraftRadius(null);
                  // Cache name/city if we don't have them yet so the panel
                  // shows a loading state then hydrates.
                  if (!clientDetails[id]) {
                    setClientDetails((prev) => ({
                      ...prev,
                      [id]: { userId: id, uncovered, loading: true },
                    }));
                    fetchClientDetail(id)
                      .then((detail) =>
                        setClientDetails((prev) => ({
                          ...prev,
                          [id]: { ...prev[id], ...detail, loading: false },
                        })),
                      )
                      .catch(() =>
                        setClientDetails((prev) => ({
                          ...prev,
                          [id]: { ...prev[id], loadError: true, loading: false },
                        })),
                      );
                  } else {
                    // Refresh the uncovered flag in case it changed since
                    // we last cached the row.
                    setClientDetails((prev) => ({
                      ...prev,
                      [id]: { ...prev[id], uncovered },
                    }));
                  }
                }}
              >
                <div
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    background: dotColor,
                    border: '2px solid #fff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                    cursor: 'pointer',
                  }}
                />
              </Marker>
            );
          })}

          {/* Popups removed — provider + client details render in the left panel */}
        </Map>

        {/* Page-scoped layout polish — collapses PrivateLayout's footer top
            margin while this page is mounted so the © line sits tight against
            the bottom of the map. Reverts automatically on navigation away. */}
        <style>{`
          footer p.text-center.mt-5 {
            margin-top: 0.4rem !important;
            margin-bottom: 0.4rem !important;
          }
        `}</style>

        {/* Collapsed-to-ball button — shows when panelOpen is false. */}
        {!panelOpen && (
          <button
            type="button"
            onClick={() => setPanelOpen(true)}
            aria-label="Expand Coverage Map panel"
            style={{
              position: 'absolute',
              top: 16,
              left: 16,
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.96)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              boxShadow:
                '0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 32px rgba(15, 23, 42, 0.12)',
              border: '1px solid rgba(15, 23, 42, 0.06)',
              color: providerColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              zIndex: 5,
              padding: 0,
              transition: 'transform 160ms ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <MapPin size={18} strokeWidth={2.5} />
          </button>
        )}

        {/* Premium scrollbar — Webkit + Firefox + edge-to-edge clip. */}
        <style>{`
          .coverage-map-panel {
            scrollbar-width: thin;
            scrollbar-color: rgba(15, 23, 42, 0.2) transparent;
          }
          .coverage-map-panel::-webkit-scrollbar {
            width: 6px;
            height: 0;
          }
          .coverage-map-panel::-webkit-scrollbar-track {
            background: transparent;
            margin: 16px 0;
          }
          .coverage-map-panel::-webkit-scrollbar-thumb {
            background-color: rgba(15, 23, 42, 0.18);
            border-radius: 999px;
            transition: background-color 200ms ease;
          }
          .coverage-map-panel::-webkit-scrollbar-thumb:hover {
            background-color: rgba(15, 23, 42, 0.35);
          }
        `}</style>
        {/* Left panel — premium analytics-dashboard treatment */}
        {panelOpen && (
        <div
          className="coverage-map-panel"
          style={{
            position: 'absolute',
            top: 16,
            left: 16,
            width: 310,
            maxHeight: 'calc(100% - 32px)',
            overflowY: 'auto',
            overflowX: 'hidden',
            zIndex: 5,
            background: 'rgba(255, 255, 255, 0.96)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            boxShadow:
              '0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 32px rgba(15, 23, 42, 0.12)',
            borderRadius: 16,
            border: '1px solid rgba(15, 23, 42, 0.06)',
            padding: 20,
            fontFeatureSettings: '"tnum" 1',
          }}
        >
          {/* Collapse — chevron in the top-right corner of the panel. */}
          <button
            type="button"
            onClick={() => setPanelOpen(false)}
            aria-label="Collapse panel"
            title="Collapse"
            style={{
              position: 'absolute',
              top: 10,
              right: 10,
              width: 24,
              height: 24,
              borderRadius: 6,
              border: 'none',
              background: 'transparent',
              color: '#94a3b8',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              transition: 'background 120ms ease, color 120ms ease',
              zIndex: 1,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(15, 23, 42, 0.06)';
              e.currentTarget.style.color = '#0f172a';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = '#94a3b8';
            }}
          >
            <ChevronLeft size={16} />
          </button>

          {/* Header */}
          <div
            className="d-flex align-items-center"
            style={{ marginBottom: 18, gap: 10 }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 10,
                background: `${providerColor}1a`,
                color: providerColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 200ms ease',
              }}
            >
              <MapPin size={16} strokeWidth={2.5} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: '#0f172a',
                  letterSpacing: '-0.01em',
                }}
              >
                Coverage Map
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>
                Real-time client + provider reach
              </div>
            </div>
            {/* Color picker — 4 overlay color dots in the top right. */}
            <div className="d-flex align-items-center" style={{ gap: 4 }}>
              {OVERLAY_COLORS.map((c) => {
                const isActive = providerColor === c.value;
                return (
                  <button
                    key={c.value}
                    type="button"
                    title={c.label}
                    aria-label={`Set overlay color to ${c.label}`}
                    onClick={() => setProviderColor(c.value)}
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      background: c.value,
                      border: '2px solid #fff',
                      boxShadow: isActive
                        ? `0 0 0 2px ${c.value}`
                        : '0 0 0 1px rgba(15, 23, 42, 0.12)',
                      cursor: 'pointer',
                      padding: 0,
                      transition: 'box-shadow 120ms ease',
                    }}
                  />
                );
              })}
            </div>
          </div>

          {/* FILTER chips — Show (layer toggles) + Map (style swap) side by side */}
          <div
            style={{
              marginBottom: 16,
              display: 'flex',
              gap: 12,
              alignItems: 'stretch',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <SectionLabel>Show</SectionLabel>
              <div className="d-flex" style={{ gap: 6, flexWrap: 'nowrap' }}>
                <Chip
                  active={showProviders}
                  onClick={() => setShowProviders((v) => !v)}
                >
                  Providers
                </Chip>
                <Chip
                  active={showClients}
                  onClick={() => setShowClients((v) => !v)}
                >
                  Clients
                </Chip>
              </div>
            </div>
            {/* Vertical divider — hairline, slightly inset so it doesn't touch
                the section label baseline or chip bottoms. */}
            <div
              aria-hidden
              style={{
                width: 1,
                alignSelf: 'stretch',
                margin: '4px 0',
                background: 'rgba(15, 23, 42, 0.12)',
              }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <SectionLabel>Map</SectionLabel>
              <div className="d-flex" style={{ gap: 6, flexWrap: 'nowrap' }}>
                <Chip
                  active={mapStyleKey === 'faded'}
                  onClick={() => setMapStyleKey('faded')}
                >
                  Faded
                </Chip>
                <Chip
                  active={mapStyleKey === 'mono'}
                  onClick={() => setMapStyleKey('mono')}
                >
                  Mono
                </Chip>
              </div>
            </div>
          </div>

          {/* SEGMENTED — Time of day */}
          <div style={{ marginBottom: 18 }}>
            <SectionLabel>Time of day</SectionLabel>
            <Segmented
              value={lightPreset}
              onChange={setLightPreset}
              options={[
                { value: 'dawn', label: 'Dawn' },
                { value: 'day', label: 'Day' },
                { value: 'dusk', label: 'Dusk' },
                { value: 'night', label: 'Night' },
              ]}
            />
          </div>

          {/* Loading / error states */}
          {isLoading && (
            <div
              className="d-flex align-items-center"
              style={{ color: '#64748b', fontSize: 13 }}
            >
              <Spinner size="sm" className="me-2" /> Loading map data...
            </div>
          )}
          {isError && (
            <div
              className="d-flex align-items-center"
              style={{ color: '#dc2626', fontSize: 13 }}
            >
              <AlertTriangle size={14} className="me-1" />
              Failed to load points.
            </div>
          )}

          {/* Summary stats */}
          {!isLoading && !isError && (
            <div
              style={{
                marginTop: 4,
                paddingTop: 14,
                borderTop: '1px solid rgba(15, 23, 42, 0.06)',
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 8,
              }}
            >
              <StatTile
                label="Providers"
                value={providers.length}
                color={providerColor}
              />
              <StatTile
                label="Clients"
                value={clients.length}
                color="#3b82f6"
              />
              <StatTile
                label="Uncovered"
                value={uncoveredSet.size}
                color="#dc2626"
                emphasize={uncoveredSet.size > 0}
              />
            </div>
          )}

          {/* Selected client detail */}
          {selectedClientId && (
            <div
              style={{
                marginTop: 18,
                padding: 14,
                background: '#f8fafc',
                borderRadius: 12,
                border: '1px solid rgba(15, 23, 42, 0.05)',
              }}
            >
              <div className="d-flex align-items-start justify-content-between" style={{ marginBottom: 6 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      fontSize: 10.5,
                      fontWeight: 600,
                      color: '#94a3b8',
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      marginBottom: 4,
                    }}
                  >
                    Client
                  </div>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 14,
                      color: '#0f172a',
                      letterSpacing: '-0.01em',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {selectedClient?.firstName || selectedClient?.lastName ? (
                      `${selectedClient.firstName || ''} ${selectedClient.lastName || ''}`.trim()
                    ) : selectedClient?.loadError ? (
                      <span style={{ color: '#dc2626' }}>Unable to load</span>
                    ) : (
                      <span style={{ color: '#94a3b8' }}>Loading...</span>
                    )}
                  </div>
                  {(selectedClient?.city || selectedClient?.state) && (
                    <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>
                      {[selectedClient.city, selectedClient.state]
                        .filter(Boolean)
                        .join(', ')}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setSelectedClientId(null)}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    padding: 4,
                    color: '#94a3b8',
                    cursor: 'pointer',
                    borderRadius: 6,
                  }}
                  aria-label="Close"
                >
                  <X size={14} />
                </button>
              </div>
              {selectedClient?.uncovered && (
                <div
                  style={{
                    marginTop: 8,
                    padding: '4px 10px',
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 600,
                    color: '#dc2626',
                    background: 'rgba(220, 38, 38, 0.08)',
                    border: '1px solid rgba(220, 38, 38, 0.2)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  <AlertTriangle size={11} /> Uncovered
                </div>
              )}
            </div>
          )}

          {/* Selected provider detail */}
          {selectedProvider && (
            <div
              style={{
                marginTop: 18,
                padding: 14,
                background: '#f8fafc',
                borderRadius: 12,
                border: '1px solid rgba(15, 23, 42, 0.05)',
              }}
            >
              <div className="d-flex align-items-start justify-content-between" style={{ marginBottom: 10 }}>
                <div>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 14,
                      color: '#0f172a',
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {selectedProvider.firstName} {selectedProvider.lastName}
                  </div>
                  <div style={{ color: '#64748b', fontSize: 12 }}>
                    {[selectedProvider.city, selectedProvider.state]
                      .filter(Boolean)
                      .join(', ')}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedProviderId(null);
                    // Match empty-map-click behavior — otherwise a failed save
                    // leaves a stale draft that bleeds into the next provider
                    // selected (briefly shows the wrong radius before hydrating).
                    setDraftRadius(null);
                  }}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    padding: 4,
                    color: '#94a3b8',
                    cursor: 'pointer',
                    borderRadius: 6,
                  }}
                  aria-label="Close"
                >
                  <X size={14} />
                </button>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: 6,
                  fontSize: 12,
                  color: '#64748b',
                }}
              >
                <span>Coverage radius</span>
                <InlineRadius
                  value={selectedProvider.radiusMiles}
                  baseValue={selectedBaseProvider?.radiusMiles}
                  disabled={!canEditRadius || updateRadius.isLoading}
                  onCommit={(next) => {
                    if (!canEditRadius) return;
                    const clamped = Math.max(1, Math.min(250, next));
                    if (clamped === selectedBaseProvider?.radiusMiles) {
                      setDraftRadius(null);
                      return;
                    }
                    persistRadius(selectedProvider.userId, clamped);
                  }}
                />
              </div>
              <Form.Range
                min={1}
                max={250}
                step={1}
                value={selectedProvider.radiusMiles}
                disabled={!canEditRadius || updateRadius.isLoading}
                onChange={(e) => setDraftRadius(Number(e.target.value))}
                onPointerUp={(e) => {
                  if (!canEditRadius) return;
                  const next = Number(e.target.value);
                  if (next === selectedBaseProvider?.radiusMiles) {
                    setDraftRadius(null);
                    return;
                  }
                  persistRadius(selectedProvider.userId, next);
                }}
              />
              {!canEditRadius && (
                <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 4 }}>
                  You don't have permission to change a provider's radius.
                </div>
              )}
            </div>
          )}
        </div>
        )}
      </div>
    </PrivateLayout>
  );
};

export default CoverageMap;
